import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AboutVera } from "@/components/AboutVera";
import { ScoreGauge } from "@/components/ScoreVisuals";
import { WHATSAPP_LINK, SOCIALS } from "@/lib/constants";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Sparkles,
  ScanLine,
  MessageCircle,
  Instagram,
  ShieldCheck,
  PartyPopper,
} from "lucide-react";

const COLOR_HEX = { groen: "#6B8E74", geel: "#E0B84B", oranje: "#D98A4E", rood: "#C86B5E" };

export default function WellnessCheck() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [levensfase, setLevensfase] = useState("");
  const [market, setMarket] = useState({});
  const [participant, setParticipant] = useState({
    voornaam: "", familienaam: "", email: "", telefoon: "", leeftijd: "", gemeente: "",
  });
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [regId, setRegId] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [bodyscan, setBodyscan] = useState(false);

  useEffect(() => {
    api.get("/questions").then((res) => setMeta(res.data)).catch(() => toast.error("Kon de vragenlijst niet laden."));
  }, []);

  const scaleOpts = useMemo(() => (meta ? Object.entries(meta.scale).map(([k, v]) => ({ v: Number(k), label: v })) : []), [meta]);

  const flow = useMemo(() => {
    if (!meta) return [];
    const steps = [{ type: "registration" }];
    let hormoneStarted = false;
    let qNumber = 0;
    const total = meta.questions.length;
    meta.questions.forEach((q) => {
      if (q.domain === meta.hormone_key && !hormoneStarted) {
        hormoneStarted = true;
        steps.push({ type: "levensfase" });
      }
      qNumber += 1;
      steps.push({ type: "question", q, number: qNumber, total });
    });
    meta.market.forEach((mq) => steps.push({ type: "market", mq }));
    return steps;
  }, [meta]);

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF7F0]">
        <Loader2 className="animate-spin text-[#6B8E74]" size={32} />
      </div>
    );
  }

  if (submitting && !result) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBF7F0] px-6 text-center" data-testid="analysis-loading">
        <Loader2 className="animate-spin text-[#6B8E74] mb-6" size={44} />
        <h2 className="text-2xl sm:text-3xl text-[#3A4A40] mb-3 font-serif">Even geduld...</h2>
        <p className="text-[#5B6B61] max-w-md leading-relaxed text-base sm:text-lg">
          Jouw persoonlijke welzijnsanalyse wordt zorgvuldig opgesteld. Dit kan een halve minuut duren — sluit dit venster niet.
        </p>
      </div>
    );
  }

  const current = flow[idx];
  const isLast = idx === flow.length - 1;
  const progress = Math.round(((idx + 1) / flow.length) * 100);
  const emailValid = /\S+@\S+\.\S+/.test(participant.email);

  const regValid =
    participant.voornaam.trim() && participant.familienaam.trim() && emailValid &&
    participant.telefoon.trim() && participant.leeftijd && Number(participant.leeftijd) >= 18 && consent;

  const goNext = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setIdx((i) => i + 1); };
  const goBack = () => { window.scrollTo({ top: 0, behavior: "smooth" }); if (idx === 0) navigate("/"); else setIdx((i) => i - 1); };

  const toggleMulti = (mq, opt) => {
    const cur = Array.isArray(market[mq.id]) ? market[mq.id] : [];
    setMarket({ ...market, [mq.id]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] });
  };

  const startMonitor = async () => {
    if (!regValid) { toast.error("Vul alle verplichte velden correct in."); return; }
    setRegistering(true);
    try {
      const { data } = await api.post("/registrations", {
        ...participant, leeftijd: Number(participant.leeftijd), consent, marketing_optin: marketing,
      });
      setRegId(data.id);
      goNext();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRegistering(false);
    }
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/submissions/${regId}/complete`, { answers, levensfase, market });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleBodyscan = async (checked) => {
    setBodyscan(checked);
    if (result?.id) { try { await api.patch(`/submissions/${result.id}/bodyscan?requested=${checked}`); } catch { /* noop */ } }
  };

  /* ---------- RESULT ---------- */
  if (result) {
    return (
      <div className="min-h-screen bg-[#FBF7F0] py-10 px-5">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-[0_16px_50px_rgba(58,74,64,0.08)] border border-[#EDE7DC] text-center animate-fade-up">
            <p className="inline-flex items-center gap-2 text-sm text-[#C88A6A] font-medium mb-4">
              <PartyPopper size={16} /> Gefeliciteerd, jouw welzijnsanalyse is klaar
            </p>
            <ScoreGauge score={result.total_score} category={result.category} />
            <p className="text-[#8FA38C] text-sm mt-4 uppercase tracking-widest">Jouw welzijnsscore</p>
            <h2 className="text-2xl sm:text-3xl text-[#3A4A40] mt-1" data-testid="result-category">{result.category}</h2>
            <p className="text-[#5B6B61] leading-relaxed mt-5 max-w-xl mx-auto" data-testid="result-interpretation">
              {result.interpretation}
            </p>
          </div>

          {/* Deelscores */}
          <div className="animate-fade-up">
            <h3 className="text-xl text-[#3A4A40] mb-4 px-1">Jouw deelscores</h3>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="category-details">
              {result.category_details.map((c) => {
                const hex = COLOR_HEX[c.color] || "#6B8E74";
                return (
                  <div key={c.key} data-testid={`category-card-${c.key}`} className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgba(58,74,64,0.05)] border border-[#EDE7DC]">
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-[#3A4A40] font-medium">{c.label}</span>
                      <span className="text-lg font-serif" style={{ color: hex }}>{c.score}<span className="text-xs text-[#8FA38C]">/100</span></span>
                    </div>
                    <div className="h-2.5 rounded-full bg-[#EDEAE1] overflow-hidden mb-2">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${c.score}%`, backgroundColor: hex }} />
                    </div>
                    <p className="text-sm text-[#5B6B61]">{c.explanation}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI tips */}
          <div className="bg-[#F1EEE5] rounded-[28px] p-6 sm:p-10 border border-[#E5DFD2] animate-fade-up" data-testid="ai-tips">
            <div className="flex items-center gap-2 mb-5">
              <span className="bg-[#6B8E74] text-white rounded-full p-2"><Sparkles size={18} /></span>
              <h3 className="text-xl sm:text-2xl text-[#3A4A40]">Jouw persoonlijke adviezen</h3>
            </div>
            <ul className="space-y-3">
              {result.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-[#EDE7DC]">
                  <span className="bg-[#6B8E74]/10 text-[#6B8E74] rounded-full w-7 h-7 flex items-center justify-center shrink-0 text-sm font-semibold">{i + 1}</span>
                  <span className="text-[#3A4A40] text-sm leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Bodyscan */}
          <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-[0_16px_50px_rgba(58,74,64,0.08)] border-2 border-[#6B8E74]/25 animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#6B8E74]/10 text-[#6B8E74] rounded-full p-3"><ScanLine size={24} /></span>
              <div>
                <h3 className="text-xl sm:text-2xl text-[#3A4A40] leading-tight">Gratis welzijnsevaluatie + professionele bodyscan</h3>
                <p className="text-sm text-[#C88A6A] font-medium">t.w.v. €50</p>
              </div>
            </div>
            <p className="text-[#5B6B61] leading-relaxed mb-4">
              Als dank voor jouw deelname bieden wij jou vrijblijvend een gratis persoonlijke welzijnsevaluatie
              inclusief professionele bodyscan aan. Tijdens deze afspraak ontvang je inzicht in:
            </p>
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-[#5B6B61] mb-6">
              {["lichaamssamenstelling", "spiermassa", "vetpercentage", "visceraal vet", "metabolische leeftijd", "persoonlijke aandachtspunten"].map((it) => (
                <li key={it} className="flex items-center gap-2"><Check size={16} className="text-[#6B8E74] shrink-0" /> {it}</li>
              ))}
            </ul>
            <label className="flex items-start gap-3 cursor-pointer bg-[#F1EEE5] rounded-2xl p-4" data-testid="bodyscan-checkbox-label">
              <input type="checkbox" checked={bodyscan} onChange={(e) => toggleBodyscan(e.target.checked)} data-testid="bodyscan-checkbox" className="mt-1 w-5 h-5 accent-[#6B8E74]" />
              <span className="text-[#3A4A40] text-sm sm:text-base">Ja, ik ontvang graag een gratis welzijnsevaluatie inclusief bodyscan ter waarde van €50.</span>
            </label>
            {bodyscan && (
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-cta"
                className="mt-5 w-full bg-[#25D366] hover:bg-[#1fb855] text-white rounded-full px-8 py-4 text-lg font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 animate-fade-up">
                <MessageCircle size={20} /> Plan mijn gratis bodyscan via WhatsApp
              </a>
            )}
          </div>

          {/* Instagram */}
          <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="instagram-card"
            className="block bg-gradient-to-br from-[#C88A6A] to-[#B25F3E] rounded-[28px] p-6 sm:p-8 text-white animate-fade-up hover:-translate-y-0.5 transition-transform">
            <div className="flex items-center gap-4">
              <span className="bg-white/20 rounded-2xl p-3"><Instagram size={26} /></span>
              <div>
                <h3 className="text-xl sm:text-2xl leading-tight">Volg Gezond met Vera</h3>
                <p className="text-white/90 text-sm mt-1">Dagelijkse tips over rust, energie en hormonale balans — en als eerste op de hoogte van NUNNU Calm &amp; Clarity.</p>
              </div>
            </div>
          </a>

          <AboutVera compact />

          <button onClick={() => navigate("/")} data-testid="result-home-button" className="w-full text-[#5B6B61] hover:text-[#3A4A40] py-3 text-sm transition-colors">
            Terug naar start
          </button>
        </div>
      </div>
    );
  }

  /* ---------- SURVEY ---------- */
  return (
    <div className="min-h-screen bg-[#FBF7F0]">
      <div className="sticky top-0 z-30 bg-[#FBF7F0]/90 backdrop-blur-md border-b border-[#EDE7DC]">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between text-sm text-[#5B6B61] mb-2">
            <button onClick={goBack} data-testid="survey-back-button" className="inline-flex items-center gap-1 hover:text-[#3A4A40] transition-colors">
              <ArrowLeft size={16} /> Terug
            </button>
            <span data-testid="survey-progress-label">
              {current?.type === "question" ? `Vraag ${current.number} van ${current.total}` : current?.type === "registration" ? "Registratie" : "Enkele korte vragen"}
            </span>
          </div>
          <div className="h-2 rounded-full bg-[#EDEAE1] overflow-hidden">
            <div className="h-full bg-[#6B8E74] rounded-full transition-all duration-500" style={{ width: `${Math.max(progress, 4)}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 pb-28">
        {/* REGISTRATION */}
        {current?.type === "registration" && (
          <div className="animate-fade-up">
            <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-[0_16px_50px_rgba(58,74,64,0.08)] border border-[#EDE7DC]">
              <h2 className="text-2xl sm:text-3xl text-[#3A4A40] mb-2 leading-snug">Nog één stap en jouw persoonlijke welzijnsanalyse kan beginnen.</h2>
              <p className="text-[#5B6B61] mb-8">Om jouw persoonlijke welzijnsrapport te kunnen opstellen en je resultaten veilig te bewaren, vragen we eerst enkele gegevens.</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Voornaam *"><input type="text" value={participant.voornaam} onChange={(e) => setParticipant({ ...participant, voornaam: e.target.value })} data-testid="input-voornaam" className="wm-input" /></Field>
                  <Field label="Familienaam *"><input type="text" value={participant.familienaam} onChange={(e) => setParticipant({ ...participant, familienaam: e.target.value })} data-testid="input-familienaam" className="wm-input" /></Field>
                </div>
                <Field label="E-mailadres *"><input type="email" value={participant.email} onChange={(e) => setParticipant({ ...participant, email: e.target.value })} data-testid="input-email" className="wm-input" placeholder="jij@voorbeeld.be" /></Field>
                <Field label="Telefoonnummer *"><input type="tel" value={participant.telefoon} onChange={(e) => setParticipant({ ...participant, telefoon: e.target.value })} data-testid="input-telefoon" className="wm-input" placeholder="+32 ..." /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Leeftijd *"><input type="number" min="18" value={participant.leeftijd} onChange={(e) => setParticipant({ ...participant, leeftijd: e.target.value })} data-testid="input-leeftijd" className="wm-input" placeholder="18+" /></Field>
                  <Field label="Gemeente"><input type="text" value={participant.gemeente} onChange={(e) => setParticipant({ ...participant, gemeente: e.target.value })} data-testid="input-gemeente" className="wm-input" /></Field>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer mt-6" data-testid="consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="consent-checkbox" className="mt-1 w-5 h-5 accent-[#6B8E74] shrink-0" />
                <span className="text-sm text-[#5B6B61]">Ik ga akkoord met het <a href="/privacy" target="_blank" className="text-[#6B8E74] underline">privacybeleid</a> en geef toestemming om mijn persoonlijke welzijnsrapport te ontvangen.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer mt-3" data-testid="marketing-label">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="marketing-checkbox" className="mt-1 w-5 h-5 accent-[#6B8E74] shrink-0" />
                <span className="text-sm text-[#5B6B61]">Ik ontvang graag tips van Gezond met Vera en blijf graag als eerste op de hoogte van de lancering van NUNNU Calm &amp; Clarity.</span>
              </label>

              <button onClick={startMonitor} disabled={!regValid || registering} data-testid="start-monitor-button"
                className="mt-8 w-full bg-[#6B8E74] hover:bg-[#56745E] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-8 py-4 text-lg font-medium tracking-wide transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2">
                {registering ? <Loader2 className="animate-spin" size={20} /> : <ShieldCheck size={20} />} START MIJN WELZIJNSMONITOR
              </button>
            </div>
          </div>
        )}

        {/* QUESTION */}
        {current?.type === "question" && (
          <div className="animate-fade-up" key={current.q.id}>
            <p className="text-sm tracking-widest uppercase text-[#6B8E74] font-medium mb-2">{meta.domains[current.q.domain]}</p>
            <h2 className="text-2xl sm:text-3xl text-[#3A4A40] mb-8 leading-snug">{current.q.text}</h2>
            <div className="flex justify-between gap-2 sm:gap-3" data-testid={`question-${current.q.id}`}>
              {scaleOpts.map((opt) => {
                const active = answers[current.q.id] === opt.v;
                return (
                  <button key={opt.v} onClick={() => { setAnswers({ ...answers, [current.q.id]: opt.v }); setTimeout(goNext, 180); }}
                    data-testid={`option-${current.q.id}-${opt.v}`} title={opt.label}
                    className={`flex-1 flex flex-col items-center justify-start gap-2 pt-4 pb-3 px-1 rounded-2xl border-2 transition-all ${active ? "border-[#6B8E74] bg-[#6B8E74]/5" : "border-[#EDE7DC] hover:border-[#6B8E74]/50"}`}>
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-base font-semibold transition-colors ${active ? "bg-[#6B8E74] text-white" : "bg-[#F1EEE5] text-[#5B6B61]"}`}>{opt.v}</span>
                    <span className={`text-[10px] sm:text-xs leading-tight text-center ${active ? "text-[#6B8E74] font-medium" : "text-[#5B6B61]"}`}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* LEVENSFASE */}
        {current?.type === "levensfase" && (
          <div className="animate-fade-up">
            <p className="text-sm tracking-widest uppercase text-[#6B8E74] font-medium mb-2">{meta.domains[meta.hormone_key]}</p>
            <h2 className="text-2xl sm:text-3xl text-[#3A4A40] mb-8 leading-snug" data-testid="levensfase-question">{meta.levensfase.question}</h2>
            <div className="flex flex-wrap gap-3">
              {meta.levensfase.options.map((opt, i) => {
                const active = levensfase === opt;
                return (
                  <button key={opt} onClick={() => setLevensfase(opt)} data-testid={`levensfase-option-${i}`}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full border-2 text-left transition-all ${active ? "border-[#6B8E74] bg-[#6B8E74]/5 text-[#6B8E74] font-medium" : "border-[#EDE7DC] text-[#3A4A40] hover:border-[#6B8E74]/50"}`}>
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-[#6B8E74]" : "border-[#C9C2B4]"}`}>{active && <span className="w-2.5 h-2.5 rounded-full bg-[#6B8E74]" />}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
            <button onClick={goNext} disabled={!levensfase} data-testid="levensfase-next"
              className="mt-6 w-full bg-[#6B8E74] hover:bg-[#56745E] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-8 py-4 text-lg font-medium transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2">
              Volgende <ArrowRight size={20} />
            </button>
          </div>
        )}

        {/* MARKET */}
        {current?.type === "market" && (
          <div className="animate-fade-up" key={current.mq.id}>
            <p className="text-sm tracking-widest uppercase text-[#C88A6A] font-medium mb-2">Marktonderzoek</p>
            <h2 className="text-2xl sm:text-3xl text-[#3A4A40] mb-8 leading-snug">{current.mq.question}</h2>
            <div className="flex flex-wrap gap-3" data-testid={`market-${current.mq.id}`}>
              {current.mq.options.map((opt, i) => {
                const sel = market[current.mq.id];
                const active = Array.isArray(sel) && sel.includes(opt);
                return (
                  <button key={opt} onClick={() => toggleMulti(current.mq, opt)} data-testid={`market-${current.mq.id}-option-${i}`}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full border-2 text-left transition-all ${active ? "border-[#6B8E74] bg-[#6B8E74]/5 text-[#6B8E74] font-medium" : "border-[#EDE7DC] text-[#3A4A40] hover:border-[#6B8E74]/50"}`}>
                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${active ? "border-[#6B8E74] bg-[#6B8E74]" : "border-[#C9C2B4]"}`}>{active && <Check size={13} className="text-white" />}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
            <button onClick={isLast ? finish : goNext} disabled={submitting} data-testid={isLast ? "submit-survey-button" : "market-next"}
              className="mt-6 w-full bg-[#6B8E74] hover:bg-[#56745E] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-8 py-4 text-lg font-medium transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2">
              {submitting ? (<><Loader2 className="animate-spin" size={20} /> Jouw analyse wordt opgesteld...</>) : isLast ? (<>Bekijk mijn welzijnsscore <Sparkles size={20} /></>) : (<>Volgende <ArrowRight size={20} /></>)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#3A4A40] mb-1.5">{label}</label>
    {children}
  </div>
);
